import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import shuffle from 'lodash/shuffle';
import { CenterFrameLayout } from 'Layouts';
import CancelSurveyForm from 'Forms/CancelSurveyForm/CancelSurveyForm';
import { tealiumTrackEvent, trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { removeSubscription } from 'Api/AccountApi';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { setSubscriptionStateProperty } from 'Utils/Analytics/Amplitude';

export const CancelSurveyContainer = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [options, setOptions] = useState([]);
  const { activeSubscriptionId, hasActiveSubscription } = useSelector((s) => s.subscription);
  const { routes, strings } = useSelector((s) => s.appConfig);

  const initialCheckboxOptions = strings.cancelationSurveyOptions;

  const surveyOptions = useMemo(() => {
    return shuffle(initialCheckboxOptions).map((text) => ({ text, selected: false }));
  }, [initialCheckboxOptions]);

  const selectedReasons = useMemo(() => {
    let selections = options.map((option) => `${option.text}:${option.selected}`).join(',');
    if (message) {
      selections += `,${message}:true`;
    }
    return selections;
  }, [options, message]);

  const handleFormStarted = () => {
    tealiumTrackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_CANCEL_SURVEY_STARTED_OLD);
    trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_CANCEL_SURVEY_STARTED);
    return {
      Source: 'Settings',
      'Reasons Displayed': options.map((option) => option.text).join(','),
    };
  };

  const handleFormSubmitted = () => {
    return {
      Source: 'Settings',
      'Reasons Selected': selectedReasons,
      'Reason Text': message,
    };
  };

  const handleFormCompleted = () => {
    const trackingProperties = {
      page_item_title: 'Cancel my subscription',
      page_action_type: selectedReasons,
    };

    trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_CANCEL_SURVEY_COMPLETED, trackingProperties, trackingProperties);
    return {
      Source: 'Settings',
      'Reasons Selected': selectedReasons,
      'Reason Text': message,
    };
  };

  const handleCheck = (selectedText) => {
    const newCheckboxOptions = options.map((option) => {
      if (option.text === selectedText) {
        return { ...option, selected: !option.selected };
      }
      return option;
    });
    setOptions(newCheckboxOptions);
  };

  const handleMsg = (text) => {
    const msg = text.trim();
    if (msg.length) {
      setMessage(msg);
    }
  };

  const handleContinue = () => {
    // this function is called when user bails out on cancelation flow.
    const trackingProperties = {
      page_item_title: 'Keep my subscription',
      page_action_type: selectedReasons,
    };

    trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_CANCEL_SURVEY_COMPLETED, trackingProperties, trackingProperties);
    return navigate(routes.myAccount);
  };

  const handleCancel = async () => {
    try {
      const isValidCancel = options.some((option) => option.selected) || message;
      if (!isValidCancel) {
        throw 'Please select at least one response.';
      }

      await removeSubscription(activeSubscriptionId);
      setSubscriptionStateProperty('pending cancel');
      navigate(routes.cancelConfirmation);
    } catch (error) {
      return onSubmitReactFormError(error);
    }
  };

  useEffect(() => {
    setOptions(surveyOptions);
  }, [surveyOptions]);

  if (!hasActiveSubscription) {
    return <Navigate replace to={routes.myAccount} />;
  }

  return (
    <CenterFrameLayout title="Before you go" showLoader={false}>
      <CancelSurveyForm
        handleCheck={handleCheck}
        handleMsg={handleMsg}
        handleContinue={handleContinue}
        onFormSubmit={handleCancel}
        started={handleFormStarted}
        submitted={handleFormSubmitted}
        completed={handleFormCompleted}
        checkboxOptions={options}
      />
    </CenterFrameLayout>
  );
};
